import moment from 'moment'
import javaTimeFormat from 'ui-io/java-time-format'

export type TimeRange = [Date | null, Date | null]

export const TIME_RANGE_OPTIONS = [
  'Past 1 hour',
  'Past 2 hours',
  'Past 24 hours',
  'Past 7 days',
  'Today',
  'Yesterday',
  'This month',
  'Last month',
] as const

export type TimeRangeOption = typeof TIME_RANGE_OPTIONS[number]

// drops milliseconds and round to start of or end of a day/hour
export const roundTime = (
  date: Date | null,
  startOrEndOf: 'startOf' | 'endOf',
  unit: 'month' | 'day' | 'hour',
) => (date ? moment(date)[startOrEndOf](unit).startOf('second').toDate() : date)

const makeTimeRange = (
  dateFrom: Date | null,
  dateTo: Date | null,
): TimeRange => [dateFrom, dateTo]

export type TimeRangeDict = Record<TimeRangeOption, TimeRange>
export const getTimeRangeDict = (now: Date) => {
  const _endOfCurrentHour = moment(now).endOf('hour')
  const getEndOfCurrentHour = () => _endOfCurrentHour.clone()
  const rangeMap: TimeRangeDict = {
    'Past 1 hour': makeTimeRange(
      roundTime(
        getEndOfCurrentHour().subtract(1, 'hours').toDate(),
        'startOf',
        'hour',
      ),
      roundTime(getEndOfCurrentHour().toDate(), 'endOf', 'hour'),
    ),
    'Past 2 hours': makeTimeRange(
      roundTime(
        getEndOfCurrentHour().subtract(2, 'hours').toDate(),
        'startOf',
        'hour',
      ),
      roundTime(getEndOfCurrentHour().toDate(), 'endOf', 'hour'),
    ),
    'Past 24 hours': makeTimeRange(
      roundTime(
        getEndOfCurrentHour().subtract(24, 'hours').toDate(),
        'startOf',
        'hour',
      ),
      roundTime(getEndOfCurrentHour().toDate(), 'endOf', 'hour'),
    ),
    'Past 7 days': makeTimeRange(
      roundTime(
        getEndOfCurrentHour().subtract(7, 'days').toDate(),
        'startOf',
        'day',
      ),
      roundTime(getEndOfCurrentHour().toDate(), 'endOf', 'day'),
    ),
    Today: makeTimeRange(
      roundTime(getEndOfCurrentHour().toDate(), 'startOf', 'day'),
      roundTime(getEndOfCurrentHour().toDate(), 'endOf', 'day'),
    ),
    Yesterday: makeTimeRange(
      roundTime(
        getEndOfCurrentHour().subtract(1, 'day').toDate(),
        'startOf',
        'day',
      ),
      roundTime(
        getEndOfCurrentHour().subtract(1, 'day').toDate(),
        'endOf',
        'day',
      ),
    ),
    'This month': makeTimeRange(
      roundTime(getEndOfCurrentHour().toDate(), 'startOf', 'month'),
      roundTime(getEndOfCurrentHour().toDate(), 'endOf', 'month'),
    ),
    'Last month': makeTimeRange(
      roundTime(
        getEndOfCurrentHour().subtract(1, 'month').toDate(),
        'startOf',
        'month',
      ),
      roundTime(
        getEndOfCurrentHour().subtract(1, 'month').toDate(),
        'endOf',
        'month',
      ),
    ),
  }
  return rangeMap
}

const areEqualDates = (a: Date | null, b: Date | null) =>
  a?.getTime() === b?.getTime()
export const areEqualTimeRanges = (a: TimeRange, b: TimeRange) =>
  areEqualDates(a[0], b[0]) && areEqualDates(a[1], b[1])

export const dateToJavaDateStr = (date?: Date | null) =>
  date ? moment(date).format(javaTimeFormat) : undefined
